/**
 * React Query hooks for Leads email templates.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { LeadsCreateEmailTemplateInput, LeadsEmailTemplate } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

interface UseEmailTemplatesOptions {
  search?: string;
  limit?: number;
}

function buildParams(options: UseEmailTemplatesOptions): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (options.search) params.search = options.search;
  if (options.limit !== undefined) params.limit = options.limit;
  return params;
}

export function useEmailTemplates(options: UseEmailTemplatesOptions = {}) {
  const queryClient = useQueryClient();
  const filters = buildParams(options);

  const query = useQuery<{ templates: LeadsEmailTemplate[]; total: number }>({
    queryKey: queryKeys.leads.emailTemplates.list(filters),
    queryFn: () =>
      api.listLeadsEmailTemplates(Object.keys(filters).length > 0 ? filters : undefined),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.leads.emailTemplates.all() });

  const createMutation = useMutation({
    mutationFn: (input: LeadsCreateEmailTemplateInput) => api.createLeadsEmailTemplate(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create template', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<LeadsCreateEmailTemplateInput> }) =>
      api.updateLeadsEmailTemplate(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to update template', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLeadsEmailTemplate(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete template', variant: 'destructive' }),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load email templates',
      variant: 'destructive',
    });
  }

  return {
    templates: query.data?.templates ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createTemplate: (input: LeadsCreateEmailTemplateInput) => createMutation.mutateAsync(input),
    updateTemplate: (id: string, input: Partial<LeadsCreateEmailTemplateInput>) =>
      updateMutation.mutateAsync({ id, input }),
    deleteTemplate: (id: string) => deleteMutation.mutateAsync(id),
    previewTemplate: (id: string, variables: Record<string, string>) =>
      api.previewLeadsEmailTemplate(id, variables),
  };
}
